import { useCallback, useEffect, useState } from 'react';

import {
  ACTIVITY_PANEL_EVENTS,
  ACTIVITY_PANEL_PROPERTIES,
} from '@/constants/analytics/activity-panel-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';

const PINNED_EVENTS_STORAGE_KEY = 'matter-pinned-events';

interface PinnedEventData {
  eventId: string;
  matterId: string;
  pinnedAt: string;
}

type PinnedEventsMap = Record<string, PinnedEventData[]>;

/**
 * Hook for managing pinned events per matter in localStorage
 */
export const usePinnedEvents = (matterId: string) => {
  const [pinnedEventIds, setPinnedEventIds] = useState<Set<string>>(new Set());
  const capture = usePostHogCapture();

  // Load pinned events from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PINNED_EVENTS_STORAGE_KEY);
      if (stored) {
        const allPinned: PinnedEventsMap = JSON.parse(stored);
        const matterPinned = allPinned[matterId] || [];
        setPinnedEventIds(new Set(matterPinned.map((p) => p.eventId)));
      }
    } catch (error) {
      console.error('Failed to load pinned events from localStorage:', error);
    }
  }, [matterId]);

  // Save pinned events to localStorage
  const savePinnedEvents = useCallback(
    (eventIds: Set<string>) => {
      try {
        const stored = localStorage.getItem(PINNED_EVENTS_STORAGE_KEY);
        const allPinned: PinnedEventsMap = stored ? JSON.parse(stored) : {};

        // Update this matter's pinned events
        allPinned[matterId] = Array.from(eventIds).map((eventId) => ({
          eventId,
          matterId,
          pinnedAt: new Date().toISOString(),
        }));

        localStorage.setItem(
          PINNED_EVENTS_STORAGE_KEY,
          JSON.stringify(allPinned),
        );
      } catch (error) {
        console.error('Failed to save pinned events to localStorage:', error);
      }
    },
    [matterId],
  );

  const isPinned = useCallback(
    (eventId: string) => pinnedEventIds.has(eventId),
    [pinnedEventIds],
  );

  const togglePin = useCallback(
    (eventId: string, activityType?: string) => {
      setPinnedEventIds((prev) => {
        const next = new Set(prev);
        const wasPinned = next.has(eventId);

        if (wasPinned) {
          next.delete(eventId);
        } else {
          next.add(eventId);
        }
        savePinnedEvents(next);

        // Track analytics
        capture(
          wasPinned
            ? ACTIVITY_PANEL_EVENTS.ACTIVITY_UNPINNED
            : ACTIVITY_PANEL_EVENTS.ACTIVITY_PINNED,
          {
            [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
            [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'activity_panel',
            [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
            [ACTIVITY_PANEL_PROPERTIES.ACTIVITY_ID]: eventId,
            [ACTIVITY_PANEL_PROPERTIES.ACTIVITY_TYPE]: activityType,
            [ACTIVITY_PANEL_PROPERTIES.PINNED_COUNT]: next.size,
          },
        );

        return next;
      });
    },
    [savePinnedEvents, capture, matterId],
  );

  const pinEvent = useCallback(
    (eventId: string) => {
      setPinnedEventIds((prev) => {
        if (prev.has(eventId)) return prev;
        const next = new Set(prev);
        next.add(eventId);
        savePinnedEvents(next);
        return next;
      });
    },
    [savePinnedEvents],
  );

  const unpinEvent = useCallback(
    (eventId: string) => {
      setPinnedEventIds((prev) => {
        if (!prev.has(eventId)) return prev;
        const next = new Set(prev);
        next.delete(eventId);
        savePinnedEvents(next);
        return next;
      });
    },
    [savePinnedEvents],
  );

  return {
    pinnedEventIds,
    isPinned,
    togglePin,
    pinEvent,
    unpinEvent,
  };
};
